/**
 * User model translations
 *
 * Translation keys follow the pattern: models.user.{category}.{key}
 * Categories: fields, tabs, blocks, badges, actions, filters
 *
 * Note: vue-i18n resolves dots as nested paths, so nested field keys
 * like "employeeProfile.app_data.availability.monday" must be structured
 * as nested objects, not dot-notation string keys.
 */
export default {
  en: {
    // Model names
    singular: "User",
    plural: "Users",

    // Tabs
    tabs: {
      general: "General",
      private_info: "Private Information",
      availability: "Availability",
      notices: "Notices",
      requests: "Requests",
      schedule: "Schedule",
      communications: "Communications"
    },

    // Blocks
    blocks: {
      avatar: "Profile Picture",
      basic_info: "Basic Information",
      private_info: "Private Information",
      employment: "Employment",
      roles: "Roles",
      availability: "Availability",
      status: "Status & Role",
      comment: "Comment",
      comments: "Comments",
      private_documents: "Documents Privés",
      EmployeeProfile: "Employee Availability"
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      // Basic Info & Private Info - nested under profile
      profile: {
        first_name: "First Name",
        last_name: "Last Name",
        birthday: "Birthday"
      },
      avatar: "Profile Picture",
      email: "Email",
      username: "Username",
      password: "Password",
      full_name: "Full Name",
      phone: "Phone",
      mobile: "Mobile",

      infos: {
        birthday: "Birthday",
        comment: "Comment"
      },
      homeAddress: {
        street_number: "Street Number",
        address: "Address",
        city: "City",
        zip: "Postal Code",
        full_address: "Address"
      },

      // Employment
      hire_date: "Hire Date",
      employee_number: "Payroll Number",
      heurePunch: "Punch Time",
      roles: "Role",
      is_active: "Active",
      hideRapport: "Hide from Reports",
      use_punch_time_as_schedule: "Use Punch Time as Schedule",
      disable_memos: "Disable Memos",
      created_at: "Created At",
      updated_at: "Updated At",

      // Domain Preset: EmployeeProfile fields
      // Nested to match: employeeProfile.availability.{day}
      employeeProfile: {
        availability: {
          monday: "Monday",
          tuesday: "Tuesday",
          wednesday: "Wednesday",
          thursday: "Thursday",
          friday: "Friday",
          saturday: "Saturday",
          sunday: "Sunday"
        },
        // App-specific fields in app_data
        app_data: {
          employee_since: "Employee Since",
          punch_time: "Punch Time",
          no_punch: "Exempt from Punch",
          hide_report: "Hide from Reports",
          use_punch_time_as_schedule: "Use Punch Time as Schedule"
        }
      }
    },

    // Badges (for select options and badge columns)
    badges: {
      employee: "Employee",
      coach: "Coach",
      inspector: "Inspector",
      superadmin: "Developer",
      admin: "Administrator",
      director: "Director",
      true: "Yes",
      false: "No"
    },

    // Filters
    filters: {
      is_active: "Status",
      roles: "Role"
    },

    // Options (for select/toggleGroup fields)
    // Pattern: options.{fieldKey}.{value}
    options: {
      roles: {
        employee: "Employee",
        coach: "Coach",
        inspector: "Inspector",
        superadmin: "Developer",
        admin: "Administrator",
        director: "Director"
      },
      employeeProfile: {
        roles: {
          employee: "Employee",
          coach: "Coach",
          inspector: "Inspector",
          superadmin: "Developer",
          admin: "Administrator",
          director: "Director"
        },
        availability: {
          monday: { yes: "Yes", no: "No", day_off: "Day Off" },
          tuesday: { yes: "Yes", no: "No", day_off: "Day Off" },
          wednesday: { yes: "Yes", no: "No", day_off: "Day Off" },
          thursday: { yes: "Yes", no: "No", day_off: "Day Off" },
          friday: { yes: "Yes", no: "No", day_off: "Day Off" },
          saturday: { yes: "Yes", no: "No", day_off: "Day Off" },
          sunday: { yes: "Yes", no: "No", day_off: "Day Off" }
        }
      }
    }
  },

  fr: {
    // Model names
    singular: "Employé",
    plural: "Employés",

    // Tabs
    tabs: {
      general: "Général",
      private_info: "Informations privées",
      availability: "Disponibilité",
      notices: "Avis",
      requests: "Demandes",
      schedule: "Horaire",
      communications: "Communications"
    },

    // Blocks
    blocks: {
      avatar: "Photo de profil",
      basic_info: "Informations de base",
      private_info: "Informations privées",
      employment: "Emploi",
      roles: "Rôles",
      availability: "Disponibilité",
      status: "Statut et rôle",
      comment: "Commentaire",
      comments: "Commentaires",
      private_documents: "Documents",
      EmployeeProfile: "Disponibilité de l'employé"
    },

    // Fields - nested structure for dot-notation keys
    fields: {
      // Basic Info & Private Info
      profile: {
        first_name: "Prénom",
        last_name: "Nom",
        birthday: "Date de naissance"
      },
      avatar: "Photo de profil",
      email: "Courriel",
      username: "Nom d'utilisateur",
      password: "Mot de passe",
      full_name: "Nom complet",
      phone: "Téléphone",
      mobile: "Mobile",

      infos: {
        birthday: "Date de naissance",
        comment: "Commentaire"
      },
      homeAddress: {
        street_number: "Numéro civique",
        address: "Adresse",
        city: "Ville",
        zip: "Code postal",
        full_address: "Adresse"
      },

      // Employment
      hire_date: "Date d'embauche",
      employee_number: "Numéro de paie",
      heurePunch: "Heure de punch",
      roles: "Rôle",
      is_active: "Actif",
      hideRapport: "Masquer des rapports",
      use_punch_time_as_schedule: "Utiliser l'heure d'entrée comme heure de punch",
      disable_memos: "Désactiver les mémos",
      created_at: "Créé le",
      updated_at: "Modifié le",

      // Domain Preset: EmployeeProfile fields
      // Nested to match: employeeProfile.availability.{day}
      employeeProfile: {
        availability: {
          monday: "Lundi",
          tuesday: "Mardi",
          wednesday: "Mercredi",
          thursday: "Jeudi",
          friday: "Vendredi",
          saturday: "Samedi",
          sunday: "Dimanche"
        },
        // App-specific fields in app_data
        app_data: {
          employee_since: "Employé depuis",
          punch_time: "Heure de punch",
          no_punch: "Exempté de pointage",
          hide_report: "Masquer des rapports",
          use_punch_time_as_schedule: "Utiliser l'heure d'entrée comme heure de punch"
        }
      }
    },

    // Badges
    badges: {
      employee: "Employé",
      coach: "Coach",
      inspector: "Inspecteur",
      superadmin: "Développeur",
      admin: "Administrateur",
      director: "Directeur",
      true: "Oui",
      false: "Non"
    },

    // Filters
    filters: {
      is_active: "Statut",
      roles: "Rôle"
    },

    // Options (for select/toggleGroup fields)
    options: {
      roles: {
        employee: "Employé",
        coach: "Coach",
        inspector: "Inspecteur",
        superadmin: "Développeur",
        admin: "Administrateur",
        director: "Directeur"
      },
      employeeProfile: {
        roles: {
          employee: "Employé",
          coach: "Coach",
          inspector: "Inspecteur",
          superadmin: "Développeur",
          admin: "Administrateur",
          director: "Directeur"
        },
        availability: {
          monday: { yes: "Oui", no: "Non", day_off: "Congé" },
          tuesday: { yes: "Oui", no: "Non", day_off: "Congé" },
          wednesday: { yes: "Oui", no: "Non", day_off: "Congé" },
          thursday: { yes: "Oui", no: "Non", day_off: "Congé" },
          friday: { yes: "Oui", no: "Non", day_off: "Congé" },
          saturday: { yes: "Oui", no: "Non", day_off: "Congé" },
          sunday: { yes: "Oui", no: "Non", day_off: "Congé" }
        }
      }
    }
  }
};
